import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ACTIVE_USER } from '@modules/auth/providers/auth.provider';
import { Observable, tap } from 'rxjs';
import { User } from '@modules/user/models/user.model';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  CompanyAuditorInput,
  CompanySecretaryInput,
  OfficerTypeEnum,
  UpdateCompanyAuditorInput,
  UpdateCompanySecretaryInput,
} from '@generated/graphql';
import { cloneDeep, startCase } from 'lodash-es';
import { CommonService } from '@shared/services/common.service';
import { COMMON_SERVICE_CONFIG } from '@shared/providers/common-service.provider';
import { Company } from '@modules/company/models/company.model';
import { SupportedCountryService } from '@modules/supported-country/services/supported-country.service';
import { SupportedCountry } from '@modules/supported-country/models/supported-country.model';
import { CompanyEventService } from '@modules/company/services/company-event.service';
import { OfficerIndividualForm } from '@shared/classes/officer/officer-individual-form.class';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { OfficerIndividualFormType } from '@modules/officer/models/form/officer-individual-form-type';
import { hasMatDatePickerError, Validators } from '@config/validators.config';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { InvalidControlScrollDirective } from '@shared/share-directives/directives/invalid-control-scroll.directive';
import { CompanySecretary } from '@modules/company/models/company-secretary.model';
import { OfficerPerson } from '@modules/officer/models/officer-person.model';
import {
  formatDateTime,
  maxDate,
  minDate,
  parseAnyDate,
} from '@shared/helpers/date-time.helpers';
import { CompanyAuditor } from '@modules/company/models/company-auditor.model';
import { SnackBarTypeEnum } from '@shared/types/commonEnum';
import { DateTime } from 'luxon';
import { GraphQLErrors } from '@apollo/client/errors';

type AuthorizedOfficerType = OfficerTypeEnum.Auditor | OfficerTypeEnum.Secretary;
type AuthorizedOfficerForm = CompanySecretaryForm | AuditorForm;

export type AuthorizedOfficerDialogData = {
  officerType: AuthorizedOfficerType;
  company: Company;
  item?: CompanySecretary | CompanyAuditor;
};

export type CompanySecretaryForm = FormGroup<{
  companyId: FormControl<string>;
  dateFrom: FormControl<Date>;
  dateTo: FormControl<Date>;
  officer: FormGroup<OfficerIndividualFormType>;
}>;

export type AuditorForm = FormGroup<{
  companyId: FormControl<string>;
  dateFrom: FormControl<Date>;
  dateTo: FormControl<Date>;
  name: FormControl<string>;
}>;

@UntilDestroy()
@Component({
  providers: [COMMON_SERVICE_CONFIG],
  selector: 'app-authorized-officer-dialog',
  templateUrl: './authorized-officer-dialog.component.html',
  styleUrls: ['./authorized-officer-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuthorizedOfficerDialogComponent implements OnInit {
  static width = '40%';

  @ViewChild(InvalidControlScrollDirective)
  scrollEl: InvalidControlScrollDirective;

  public officerIndividualForm: OfficerIndividualForm | null = null;
  public form: AuthorizedOfficerForm | null = null;
  public companySecretaryForm: CompanySecretaryForm | null = null;
  public auditorForm: AuditorForm | null = null;

  readonly minDate = minDate;
  readonly maxDate = maxDate;
  readonly OfficerTypeEnum = OfficerTypeEnum;

  get minDateOfDateTo(): Date | null {
    const dateFrom = this.form?.controls.dateFrom.value;

    if (!dateFrom) {
      return null;
    }

    const nextDate = cloneDeep(dateFrom);
    nextDate.setDate(nextDate.getDate() + 1);

    return nextDate;
  }

  get supportedCountry$(): Observable<SupportedCountry> {
    return this.supportedCountryService.findByCountryId$(
      this.data.company.country.id,
    );
  }

  get officerType(): AuthorizedOfficerType {
    return this.data.officerType;
  }

  get company(): Company {
    return this.data.company;
  }

  get item(): AuthorizedOfficerDialogData['item'] {
    return this.data.item;
  }

  get submitActionLabel(): string {
    return this.item ? 'Update' : 'Add';
  }

  get dialogTitle(): string {
    return [this.submitActionLabel, this.officerTypeLabel].join(' ');
  }

  get officerTypeLabel(): string {
    return startCase(this.officerType.toLowerCase());
  }

  constructor(
    public readonly commonService: CommonService,
    @Inject(ACTIVE_USER) public readonly activeUser$: Observable<User>,
    @Inject(MAT_DIALOG_DATA) public readonly data: AuthorizedOfficerDialogData,
    protected readonly supportedCountryService: SupportedCountryService,
    protected readonly dialogRef: MatDialogRef<AuthorizedOfficerDialogComponent>,
    private readonly companyEventService: CompanyEventService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.registerFeedbackHandlers();
  }

  onCloseDialog(): void {
    this.dialogRef.close();
  }

  onSubmit(): void {
    switch (this.officerType) {
      case OfficerTypeEnum.Secretary: {
        this.submitSecretary();
        return;
      }
      case OfficerTypeEnum.Auditor: {
        this.submitAuditor();
        return;
      }
      default: {
        throw new Error(`Unsupported authorized officer type: ${this.officerType}`);
      }
    }
  }

  hasMatPickerError(control: AbstractControl): boolean {
    return hasMatDatePickerError(control);
  }

  private initializeForm(): void {
    switch (this.officerType) {
      case OfficerTypeEnum.Secretary: {
        this.initializeSecretaryForm();
        break;
      }
      case OfficerTypeEnum.Auditor: {
        this.initializeAuditorForm();
        break;
      }
      default: {
        this.dialogRef.close();
        throw new Error(`Can't initialize form`);
      }
    }

    this.cdr.markForCheck();
  }

  private initializeSecretaryForm(): void {
    const { fb } = this.commonService;

    this.officerIndividualForm = new OfficerIndividualForm();
    this.officerIndividualForm.createOfficerForm();

    const secretaryForm = fb.group({
      companyId: fb.control(this.company.id, [Validators.required]),
      dateFrom: fb.control(DateTime.now().toJSDate(), [Validators.required]),
      dateTo: fb.control(null),
      officer: this.officerIndividualForm.officerForm,
    }) as CompanySecretaryForm;

    secretaryForm.controls.officer.addValidators([Validators.required]);
    secretaryForm.updateValueAndValidity();

    this.companySecretaryForm = secretaryForm;
    this.form = secretaryForm;

    if (
      this.item instanceof CompanySecretary &&
      this.item.officer.officerProfile instanceof OfficerPerson
    ) {
      this.patchSecretaryForm(this.item);
    }
  }

  private initializeAuditorForm(): void {
    const { fb } = this.commonService;

    const auditorForm = fb.group({
      name: fb.control('', [Validators.required]),
      companyId: fb.control(this.company.id, [Validators.required]),
      dateFrom: fb.control(DateTime.now().toJSDate(), [Validators.required]),
      dateTo: fb.control(null),
    }) as AuditorForm;

    this.auditorForm = auditorForm;
    this.form = auditorForm;

    if (this.item instanceof CompanyAuditor) {
      this.patchAuditorForm(this.item);
    }
  }

  private registerFeedbackHandlers(): void {
    this.registerCreateSuccessHandler(
      this.companyEventService.onCompanySecretaryCreatedSuccess,
      "You've successfully added company secretary",
    );
    this.registerErrorHandler(
      this.companyEventService.onCompanySecretaryCreatedError,
    );

    this.registerUpdateSuccessHandler(
      this.companyEventService.onCompanySecretaryUpdatedSuccess,
    );
    this.registerErrorHandler(
      this.companyEventService.onCompanySecretaryUpdatedError,
    );

    this.registerCreateSuccessHandler(
      this.companyEventService.onCompanyAuditorCreatedSuccess,
      "You've successfully added company auditor",
    );
    this.registerErrorHandler(this.companyEventService.onCompanyAuditorCreatedError);

    this.registerUpdateSuccessHandler(
      this.companyEventService.onCompanyAuditorUpdatedSuccess,
    );
    this.registerErrorHandler(this.companyEventService.onCompanyAuditorUpdatedError);
  }

  private registerCreateSuccessHandler<T>(
    stream: Observable<T>,
    successMessage: string,
  ): void {
    stream
      .pipe(
        untilDestroyed(this),
        tap(() => {
          this.commonService.setLoading(false);
          this.commonService.snackBarService.pushAlert(
            successMessage,
            null,
            SnackBarTypeEnum.success,
          );
          this.dialogRef.close();
          this.cdr.markForCheck();
        }),
      )
      .subscribe();
  }

  private registerUpdateSuccessHandler<T>(stream: Observable<T>): void {
    stream
      .pipe(
        untilDestroyed(this),
        tap(() => {
          this.commonService.setLoading(false);
          this.commonService.snackBarService.pushUpdateSuccessMessage();
          this.dialogRef.close();
          this.cdr.markForCheck();
        }),
      )
      .subscribe();
  }

  private registerErrorHandler(
    stream: Observable<GraphQLErrors | null>,
  ): void {
    stream
      .pipe(
        untilDestroyed(this),
        tap((errors) => {
          this.commonService.setLoading(false);
          this.commonService.snackBarService.pushErrorMessage();
          this.showValidationErrors(errors);
          this.cdr.markForCheck();
        }),
      )
      .subscribe();
  }

  private submitSecretary(): void {
    const secretaryForm = this.assertSecretaryForm();
    this.commonService.setLoading(true);

    if (this.item instanceof CompanySecretary) {
      this.companyEventService.onCompanySecretaryUpdate.next({
        input: {
          ...this.buildSecretaryInput(secretaryForm),
          id: this.item.id,
        } as UpdateCompanySecretaryInput,
      });

      return;
    }

    this.companyEventService.onCompanySecretaryCreate.next({
      input: this.buildSecretaryInput(secretaryForm),
    });
  }

  private submitAuditor(): void {
    const auditorForm = this.assertAuditorForm();
    this.commonService.setLoading(true);

    if (this.item instanceof CompanyAuditor) {
      this.companyEventService.onCompanyAuditorUpdate.next({
        input: {
          ...this.buildAuditorInput(auditorForm),
          id: this.item.id,
        } as UpdateCompanyAuditorInput,
      });

      return;
    }

    this.companyEventService.onCompanyAuditorCreate.next({
      input: this.buildAuditorInput(auditorForm),
    });
  }

  private buildSecretaryInput(
    form: CompanySecretaryForm,
  ): CompanySecretaryInput {
    return {
      ...form.getRawValue(),
      ...this.buildDateRangePayload(form),
    } as CompanySecretaryInput;
  }

  private buildAuditorInput(form: AuditorForm): CompanyAuditorInput {
    return {
      ...form.getRawValue(),
      ...this.buildDateRangePayload(form),
    } as CompanyAuditorInput;
  }

  private buildDateRangePayload(form: AuthorizedOfficerForm): {
    dateFrom: string;
    dateTo: string | null;
  } {
    return {
      dateFrom: formatDateTime(form.controls.dateFrom.value),
      dateTo: form.controls.dateTo.value
        ? formatDateTime(form.controls.dateTo.value)
        : null,
    };
  }

  private showValidationErrors(errors: GraphQLErrors | null): void {
    if (!errors || !this.form) {
      return;
    }

    const validationErrors = this.commonService.errorService.getValidationErrorObjectList(
      errors,
    );

    if (this.scrollEl) {
      validationErrors.setScrollEl(this.scrollEl);
    }

    validationErrors.showErrors(this.form, 'input');
  }

  private assertSecretaryForm(): CompanySecretaryForm {
    if (!this.companySecretaryForm || !this.officerIndividualForm) {
      throw new Error('Company secretary form has not been initialized');
    }

    return this.companySecretaryForm;
  }

  private assertAuditorForm(): AuditorForm {
    if (!this.auditorForm) {
      throw new Error('Company auditor form has not been initialized');
    }

    return this.auditorForm;
  }

  private patchSecretaryForm(item: CompanySecretary): void {
    const officerProfile = cloneDeep(item.officer.officerProfile) as OfficerPerson;
    const { address, identification } = officerProfile;
    const { nationality, officerIdentificationType } = identification;

    this.form?.patchValue({
      ...this.item,
      officer: {
        ...officerProfile,
        ...address,
        countryId: address?.country?.id,
        identification: {
          ...identification,
          expiryDate: identification.expiryDate
            ? parseAnyDate(identification.expiryDate)
            : null,
          officerIdentificationTypeId: officerIdentificationType.id,
          nationalityId: nationality.id,
        },
      },
    });

    this.form?.markAllAsTouched();
    this.cdr.markForCheck();
  }

  private patchAuditorForm(item: CompanyAuditor): void {
    this.form?.patchValue({ ...item });
    this.form?.markAllAsTouched();
    this.cdr.markForCheck();
  }
}
